import EntityModel from '@/common/models/entity-model';
import RuleActionModel from '@/common/models/rules/actions/rule-action-model';
import ScheduleInstructionModel from '@/common/models/rules/schedule-instruction-model';
import format from '@/common/format';

export class PaymentInstructionModel extends EntityModel {
    hydrate({
        method = PaymentInstructionModel.Methods.none,
        ...instructionDetails
    } = {}) {
        this.method = method;
        if (this.method !== PaymentInstructionModel.Methods.none) {
            ({value: this.value, type: this.type} = instructionDetails);
        }
    }

    static Methods = {
        none: 'none',
        partial: 'partial',
        discount: 'discount',
    };

    static Types = {
        fixed: 'fixed',
        percent: 'percent',
    };

    get summary() {
        switch (this.method) {
            case PaymentInstructionModel.Methods.none:
            default:
                return `using the full amount`;
            case PaymentInstructionModel.Methods.partial:
                if (this.type === PaymentInstructionModel.Types.fixed) {
                    return `for a partial amount of ${format.number(this.value)}$`;
                }
                return `for ${this.value}% of the original amount`;
            case PaymentInstructionModel.Methods.discount:
                if (this.type === PaymentInstructionModel.Types.fixed) {
                    return `with a discount of ${format.number(this.value)}$`;
                }
                return `with a ${this.value}% discount of the original amount`;
        }
    }

    getPayload() {
        const payload = {method: this.method};
        if (this.method !== PaymentInstructionModel.Methods.none) {
            return {
                ...payload,
                value: this.value,
                type: this.type,
            };
        }
        return payload;
    }
}

export class PaymentRetryModel extends EntityModel {
    hydrate({
        scheduleInstruction = null,
        paymentInstruction = null,
    } = {}) {
        this.scheduleInstruction = new ScheduleInstructionModel(scheduleInstruction || {});
        this.paymentInstruction = new PaymentInstructionModel(paymentInstruction || {});
    }

    get summary() {
        return `Retry ${this.scheduleInstruction.summary} ${this.paymentInstruction.summary}.`;
    }

    getPayload() {
        return {
            scheduleInstruction: this.scheduleInstruction.getPayload(),
            paymentInstruction: this.paymentInstruction.getPayload(),
        };
    }
}

export default class SchedulePaymentRetryActionModel extends RuleActionModel {
    hydrate(fields = {}) {
        super.hydrate(fields);
        const {
            attempts = [],
            afterAttemptPolicy = SchedulePaymentRetryActionModel
                .AfterAttemptPolicies.none,
            afterRetryEndPolicy = SchedulePaymentRetryActionModel
                .AfterRetryEndPolicies.cancelSubscription,
        } = fields;
        this.attempts = attempts.map(attempt => new PaymentRetryModel(attempt));
        this.afterAttemptPolicy = afterAttemptPolicy;
        this.afterRetryEndPolicy = afterRetryEndPolicy;
    }

    static AfterAttemptPolicies = {
        changeSubscriptionRenewalTime: 'change-subscription-renewal-time',
        none: 'none',
    };

    static AfterRetryEndPolicies = {
        cancelSubscription: 'cancel-subscription',
        none: 'none',
    };

    getPayload() {
        return {
            ...super.getPayload(),
            attempts: this.attempts.map(attempt => attempt.getPayload()),
            afterAttemptPolicy: this.afterAttemptPolicy,
            afterRetryEndPolicy: this.afterRetryEndPolicy,
        };
    }
}
